import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';

/**
 * Visual separator rendered as a 1px border line.
 *
 * Renders horizontally by default (border-top). When `vertical` is set,
 * renders as a vertical separator (border-right) that inherits height
 * from its container — typically a flex row.
 *
 * Exposes a `role="separator"` element with a dynamic `aria-orientation`
 * attribute for assistive technologies.
 *
 * ## Styling API
 *
 * Consumers can customize appearance via CSS custom properties:
 *
 * - `--divider-color` — color of the separator line (default: `$app-divider-color`)
 * - `--divider-width` — thickness of the separator line (default: `1px`)
 *
 * @example
 * ```html
 * <app-divider></app-divider>
 * <app-divider vertical></app-divider>
 * <app-divider class="my-3 w-100"></app-divider>
 * ```
 */
@Component({
  selector: 'app-divider',
  template: '',
  styleUrls: ['./divider.component.scss'],
  host: {
    '[class]': 'classes',
    role: 'separator',
    '[attr.aria-orientation]': 'vertical ? "vertical" : "horizontal"',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DividerComponent {
  private _vertical = false;

  /**
   * Whether the divider is rendered vertically (border-right) instead of
   * horizontally (border-top). Accepts the attribute syntax
   * (`<app-divider vertical>`) via boolean coercion.
   */
  @Input()
  get vertical(): boolean {
    return this._vertical;
  }
  set vertical(value: BooleanInput) {
    this._vertical = coerceBooleanProperty(value);
  }

  get classes(): string {
    const classList: string[] = ['divider'];
    if (this._vertical) {
      classList.push('divider--vertical');
    }
    return classList.join(' ');
  }
}
